import React, { useState, useEffect, useCallback } from 'react';
import {
  Card,
  Table,
  Button,
  Space,
  Modal,
  Form,
  Input,
  InputNumber,
  Switch,
  message,
  Popconfirm,
  Typography,
  Row,
  Col,
  Divider,
  Select,
  Tag,
  Tooltip,
} from 'antd';
import type { FormRule } from 'antd';
import {
  PlusOutlined,
  EditOutlined,
  DeleteOutlined,
  ReloadOutlined,
  PictureOutlined,
  MinusCircleOutlined,
} from '@ant-design/icons';
import { adminApi } from '@/services/api';
import { ConfigVersionSelect } from '@/components/ConfigVersionSelect';
import { configVersionScopeLabel } from '@/utils/configVersionScope';
import { apiErrorMessage } from '@/utils/apiError';
import dayjs from 'dayjs';
import {
  MAX_MIN_LEVEL,
  COHORT_PAGE_SIZE,
  cleanSlotTargeting,
  describeTargeting,
  experimentOptionsFromResponse,
  loadAllCohortSlugs,
  segmentOptions,
  slotExperimentOf,
  slotProblems,
} from './slotTargeting';
import type { ExperimentOption } from './slotTargeting';
import { cleanIosImage } from './iosImage';

const { Title, Text } = Typography;

interface CustomAdItem {
  adType: number;
  imgurl: string;
  /** iPhone players get this image instead of imgurl; for banners that show a price. */
  imgurlIos?: string;
  id: string;
  ename: string;
  isStatic?: boolean;
  canHideBuyButton?: boolean;
  link?: string;
  desc?: string;
}

interface CustomAdSlot {
  sIndex: number;
  data: CustomAdItem[];
  /** Spender segments and cohort slugs allowed to see this slot; empty or absent = everyone. */
  segments?: string[];
  /** Hide the slot from players who have bought something. */
  excludePayers?: boolean;
  /** Hide the slot below this player level (1-100); null or absent = no minimum. */
  minLevel?: number | null;
  /** Test arm: only players in this variant see the slot (control also when the test is off). */
  experiment?: { key?: string | null; variant?: string | null } | null;
  /** By player_locale country: include = only these (unplaced players left out), exclude = not these. */
  countries?: { include?: string[] | null; exclude?: string[] | null } | null;
}

// A test arm needs both the experiment and its variant, or neither. Both are
// read from the form: clearing the experiment also clears the variant, and the
// value handed to the validator can still be the old variant at that moment.
const experimentVariantRule =
  (slot: number): FormRule =>
  ({ getFieldValue }) => ({
    validator() {
      const key = getFieldValue(['mMAd', slot, 'experiment', 'key']) as string | null | undefined;
      const variant = getFieldValue(['mMAd', slot, 'experiment', 'variant']) as
        | string
        | null
        | undefined;
      if (Boolean(key) === Boolean(variant)) return Promise.resolve();
      return Promise.reject(new Error('Set both the experiment and its variant, or neither.'));
    },
  });

const problemList = (lines: string[]) => (
  <ul style={{ paddingLeft: 20, margin: 0 }}>
    {lines.map((line) => (
      <li key={line}>{line}</li>
    ))}
  </ul>
);

/** The experiments a config's slots are arms of, for the list view. */
const experimentKeysOf = (slots: CustomAdSlot[] | undefined): string[] => [
  ...new Set(
    (slots ?? [])
      .map((slot) => slotExperimentOf(slot)?.key)
      .filter((key): key is string => Boolean(key))
  ),
];

interface CustomAdsConfig {
  id: string;
  location: string;
  description?: string;
  config: {
    mMAd: CustomAdSlot[];
    mmDefAd: CustomAdItem;
  };
  /** Config version this ad slot is limited to; null = every build. */
  configVersionId?: string | null;
  /** Label of that config version; null for a shared row. */
  configVersionLabel?: string | null;
  isActive: boolean;
  priority: number;
  createdAt: string;
  updatedAt: string;
}

export const CustomAdsPage: React.FC = () => {
  const [loading, setLoading] = useState(true);
  const [configs, setConfigs] = useState<CustomAdsConfig[]>([]);
  const [modalVisible, setModalVisible] = useState(false);
  const [editingId, setEditingId] = useState<string | null>(null);
  const [form] = Form.useForm();
  // Cohort slugs for the slot segment picker; stays empty if the call fails.
  const [cohortSlugs, setCohortSlugs] = useState<string[]>([]);
  // Experiments for the test-arm picker; stays empty if the call fails (the
  // variant checks before a save are then skipped, the backend still checks).
  const [experiments, setExperiments] = useState<ExperimentOption[]>([]);

  const fetchData = useCallback(async () => {
    setLoading(true);
    try {
      const res = await adminApi.getCustomAds();
      const data = res.data;
      setConfigs(Array.isArray(data) ? data : []);
    } catch (error) {
      console.error('Failed to fetch custom ads', error);
      message.error('Failed to load custom ads');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchData();
  }, [fetchData]);

  // Loaded once. A failure only costs the cohort slugs: the four spender
  // segments are still offered and the page keeps working.
  useEffect(() => {
    let cancelled = false;
    loadAllCohortSlugs((offset) =>
      adminApi.getCohorts({ limit: COHORT_PAGE_SIZE, offset }).then((res) => res.data)
    ).then((slugs) => {
      if (!cancelled) setCohortSlugs(slugs);
    });
    return () => {
      cancelled = true;
    };
  }, []);

  useEffect(() => {
    let cancelled = false;
    adminApi
      .getExperiments()
      .then((res) => {
        if (!cancelled) setExperiments(experimentOptionsFromResponse(res.data));
      })
      .catch((error) => {
        console.error('Failed to fetch experiments for slot test arms', error);
      });
    return () => {
      cancelled = true;
    };
  }, []);

  // Form values interface for stronger typing
  interface CustomAdsFormValues {
    location: string;
    description?: string;
    priority: number;
    isActive: boolean;
    mMAd: CustomAdSlot[];
    mmDefAd: CustomAdItem;
    configVersionId?: string | null;
  }

  const handleSubmit = async (values: CustomAdsFormValues) => {
    try {
      // Reconstruct the nested structure
      // The form will likely flatten some things or we need to ensure the structure matches DTO

      // An untargeted slot is sent exactly as before targeting existed.
      const mMAd = values.mMAd?.map((slot) => {
        const cleaned = cleanSlotTargeting(slot);
        return Array.isArray(cleaned.data)
          ? { ...cleaned, data: cleaned.data.map((ad) => cleanIosImage(ad)) }
          : cleaned;
      });

      // Test arms: refuse what the backend would refuse, and ask before a
      // save that leaves some players with nothing on a slot index.
      const problems = slotProblems(mMAd, experiments);
      if (problems.errors.length > 0) {
        Modal.error({
          title: 'Fix these slots before saving',
          content: problemList(problems.errors),
          width: 640,
        });
        return;
      }
      if (problems.warnings.length > 0) {
        const confirmed = await new Promise<boolean>((resolve) => {
          Modal.confirm({
            title: 'Some players will see nothing on a slot index',
            content: problemList(problems.warnings),
            okText: 'Save anyway',
            cancelText: 'Go back',
            width: 640,
            onOk: () => resolve(true),
            onCancel: () => resolve(false),
          });
        });
        if (!confirmed) return;
      }

      const payload = {
        location: values.location,
        description: values.description,
        priority: values.priority,
        isActive: values.isActive,
        mMAd,
        // A blank iOS image is left out so it is not sent as an empty string.
        mmDefAd: values.mmDefAd ? cleanIosImage(values.mmDefAd) : values.mmDefAd,
        // Explicit payload, so this has to be listed or the picker silently
        // does nothing. null (not undefined) clears the scope back to shared.
        configVersionId: values.configVersionId ?? null,
      };

      if (editingId) {
        await adminApi.updateCustomAd(editingId, payload);
        message.success('Configuration updated');
      } else {
        await adminApi.createCustomAd(payload);
        message.success('Configuration created');
      }
      setModalVisible(false);
      form.resetFields();
      setEditingId(null);
      fetchData();
    } catch (error) {
      console.error('Failed to save configuration', error);
      // Shows the server's reason, e.g. "Unknown segment(s): dolfin".
      message.error(apiErrorMessage(error, 'Failed to save configuration'));
    }
  };

  const handleEdit = (record: CustomAdsConfig) => {
    setEditingId(record.id);
    form.setFieldsValue({
      location: record.location,
      description: record.description,
      priority: record.priority,
      isActive: record.isActive,
      mMAd: record.config.mMAd,
      mmDefAd: record.config.mmDefAd,
      configVersionId: record.configVersionId ?? undefined,
    });
    setModalVisible(true);
  };

  const handleDelete = async (id: string) => {
    try {
      await adminApi.deleteCustomAd(id);
      message.success('Configuration deleted');
      fetchData();
    } catch (error) {
      console.error('Failed to delete configuration', error);
      message.error('Failed to delete configuration');
    }
  };

  const handleToggleActive = async (id: string) => {
    try {
      await adminApi.toggleCustomAdActive(id);
      message.success('Status updated');
      fetchData();
    } catch (error) {
      console.error('Failed to update status', error);
      message.error('Failed to update status');
    }
  };

  const handleInvalidateCache = async () => {
    try {
      await adminApi.invalidateCustomAdsCache();
      message.success('Cache invalidated');
    } catch (error) {
      console.error('Failed to invalidate cache', error);
      message.error('Failed to invalidate cache');
    }
  };

  const columns = [
    {
      title: 'Location',
      dataIndex: 'location',
      key: 'location',
      render: (text: string) => <Text strong>{text}</Text>,
    },
    {
      // A scoped row reaches only the builds mapped to its config version, so
      // an edit to it is invisible on any other build.
      title: 'Config Version',
      key: 'configVersion',
      render: (_: unknown, record: CustomAdsConfig) =>
        record.configVersionId ? (
          <Tag color="blue">{configVersionScopeLabel(record)}</Tag>
        ) : (
          <Tag>{configVersionScopeLabel(record)}</Tag>
        ),
    },
    { title: 'Description', dataIndex: 'description', key: 'description' },
    {
      // A running test lives in these slots: edit its arms with care.
      title: 'Tests',
      key: 'tests',
      render: (_: unknown, record: CustomAdsConfig) => {
        const keys = experimentKeysOf(record.config?.mMAd);
        return keys.length > 0
          ? keys.map((key) => (
              <Tag key={key} color="purple">
                {key}
              </Tag>
            ))
          : null;
      },
    },
    {
      title: 'Priority',
      dataIndex: 'priority',
      key: 'priority',
      sorter: (a: CustomAdsConfig, b: CustomAdsConfig) => a.priority - b.priority,
    },
    {
      title: 'Active',
      dataIndex: 'isActive',
      key: 'isActive',
      render: (isActive: boolean, record: CustomAdsConfig) => (
        <Switch checked={isActive} onChange={() => handleToggleActive(record.id)} size="small" />
      ),
    },
    {
      title: 'Last Updated',
      dataIndex: 'updatedAt',
      key: 'updatedAt',
      render: (date: string) => dayjs(date).format('YYYY-MM-DD HH:mm'),
    },
    {
      title: 'Actions',
      key: 'actions',
      render: (_: unknown, record: CustomAdsConfig) => (
        <Space>
          <Button icon={<EditOutlined />} size="small" onClick={() => handleEdit(record)}>
            Edit
          </Button>
          <Popconfirm title="Delete this config?" onConfirm={() => handleDelete(record.id)}>
            <Button icon={<DeleteOutlined />} size="small" danger>
              Delete
            </Button>
          </Popconfirm>
        </Space>
      ),
    },
  ];

  return (
    <div style={{ padding: 24 }}>
      <Card
        title={
          <Space>
            <PictureOutlined />
            <Title level={4} style={{ margin: 0 }}>
              Custom Ads Configurations
            </Title>
          </Space>
        }
        extra={
          <Space>
            <Tooltip title="Clears the server's ad cache only. It cannot reach a device: players pick up an ad change when the Ads Config Version changes, which every save, delete or status change here bumps.">
              <Button icon={<ReloadOutlined />} onClick={handleInvalidateCache}>
                Invalidate Cache
              </Button>
            </Tooltip>
            <Button
              type="primary"
              icon={<PlusOutlined />}
              onClick={() => {
                setEditingId(null);
                form.resetFields();
                // Set default values for new entry
                form.setFieldsValue({
                  priority: 0,
                  isActive: true,
                  mmDefAd: {
                    adType: 2,
                    imgurl:
                      'https://wcc4-config.s3.ap-south-1.amazonaws.com/ads/ad-banner-copy.webp',
                    id: '1|3|0',
                    ename: 'default_ad',
                    isStatic: false,
                    canHideBuyButton: false,
                  },
                  mMAd: [
                    {
                      sIndex: 0,
                      data: [
                        {
                          adType: 2,
                          imgurl:
                            'https://wcc4-config.s3.ap-south-1.amazonaws.com/ads/ad-banner-cardcopy.webp',
                          id: '1|3|0',
                          ename: 'slot0_ad1',
                          isStatic: false,
                          canHideBuyButton: false,
                        },
                      ],
                    },
                  ],
                });
                setModalVisible(true);
              }}
            >
              New Configuration
            </Button>
          </Space>
        }
      >
        <Text type="secondary" style={{ display: 'block', marginBottom: 16 }}>
          Every save, delete or status change bumps the Ads Config Version. Players get the change
          on their next app launch or hub visit while the ads_revision_in_config_version feature
          flag is on. A row limited to a config version reaches only the builds mapped to it.
        </Text>
        <Table
          dataSource={configs}
          columns={columns}
          rowKey="id"
          loading={loading}
          pagination={{ pageSize: 10 }}
        />
      </Card>

      <Modal
        title={editingId ? 'Edit Configuration' : 'Create Configuration'}
        open={modalVisible}
        onCancel={() => {
          setModalVisible(false);
          form.resetFields();
          setEditingId(null);
        }}
        onOk={() => form.submit()}
        width={800}
        style={{ top: 20 }}
      >
        <Form form={form} layout="vertical" onFinish={handleSubmit}>
          <Form.Item
            name="configVersionId"
            label="Config Version"
            extra="Leave empty to show this to every build. Pick a config version to limit it to the app versions mapped to that version."
          >
            <ConfigVersionSelect style={{ width: '100%' }} />
          </Form.Item>

          <Row gutter={16}>
            <Col span={12}>
              <Form.Item name="location" label="Location" rules={[{ required: true }]}>
                <Select
                  placeholder="Select or type location"
                  disabled={!!editingId}
                  showSearch
                  allowClear
                  options={[
                    { value: 'main_menu', label: 'Main Menu' },
                    { value: 'shop', label: 'Shop' },
                    { value: 'match_end', label: 'Match End' },
                    { value: 'inventory', label: 'Inventory' },
                    { value: 'daily_rewards', label: 'Daily Rewards' },
                    { value: 'test', label: 'Test' },
                  ]}
                />
              </Form.Item>
            </Col>
            <Col span={12}>
              <Form.Item name="priority" label="Priority">
                <InputNumber style={{ width: '100%' }} />
              </Form.Item>
            </Col>
          </Row>

          <Form.Item name="description" label="Description">
            <Input.TextArea rows={2} />
          </Form.Item>

          <Form.Item name="isActive" valuePropName="checked" label="Active Status">
            <Switch />
          </Form.Item>

          <Divider orientation="left">Default Ad (Fallback)</Divider>
          {/* We'll just define the fields directly for mmDefAd since it's a single object */}
          <Card size="small" style={{ background: '#f0f2f5' }}>
            <Row gutter={16}>
              <Col span={8}>
                <Form.Item name={['mmDefAd', 'adType']} label="Type" rules={[{ required: true }]}>
                  <InputNumber style={{ width: '100%' }} />
                </Form.Item>
              </Col>
              <Col span={8}>
                <Form.Item name={['mmDefAd', 'id']} label="Product ID" rules={[{ required: true }]}>
                  <Input />
                </Form.Item>
              </Col>
              <Col span={8}>
                <Form.Item
                  name={['mmDefAd', 'ename']}
                  label="Event Name"
                  rules={[{ required: true }]}
                >
                  <Input />
                </Form.Item>
              </Col>
            </Row>
            <Form.Item name={['mmDefAd', 'imgurl']} label="Image URL" rules={[{ required: true }]}>
              <Input />
            </Form.Item>
            <Form.Item
              name={['mmDefAd', 'imgurlIos']}
              label="iOS Image URL (optional)"
              extra="iPhone players see this image instead; use it when the banner shows a price."
            >
              <Input allowClear />
            </Form.Item>
            <Row gutter={16}>
              <Col span={12}>
                <Form.Item name={['mmDefAd', 'isStatic']} label="Is Static" valuePropName="checked">
                  <Switch />
                </Form.Item>
              </Col>
              <Col span={12}>
                <Form.Item
                  name={['mmDefAd', 'canHideBuyButton']}
                  label="Can Hide Buy Button"
                  valuePropName="checked"
                >
                  <Switch />
                </Form.Item>
              </Col>
            </Row>
          </Card>

          <Divider orientation="left">Ad Slots</Divider>
          <Form.List name="mMAd">
            {(fields, { add, remove }) => (
              <div style={{ display: 'flex', flexDirection: 'column', rowGap: 16 }}>
                {fields.map((field) => (
                  <Card
                    key={field.key}
                    size="small"
                    title={
                      <Form.Item noStyle shouldUpdate>
                        {({ getFieldValue }) => {
                          const slot = getFieldValue(['mMAd', field.name]) ?? {};
                          return (
                            <Space size={8}>
                              <span>{`Slot ${field.name + 1} · ${describeTargeting(slot)}`}</span>
                              {slotExperimentOf(slot) ? <Tag color="purple">test arm</Tag> : null}
                            </Space>
                          );
                        }}
                      </Form.Item>
                    }
                    extra={<MinusCircleOutlined onClick={() => remove(field.name)} />}
                  >
                    <Form.Item
                      label="Slot Index"
                      name={[field.name, 'sIndex']}
                      rules={[{ required: true }]}
                    >
                      <InputNumber />
                    </Form.Item>

                    <Row gutter={16}>
                      <Col span={12}>
                        <Form.Item
                          label="Segments"
                          name={[field.name, 'segments']}
                          extra="Empty shows the slot to everyone."
                          rules={[
                            {
                              type: 'array',
                              max: 20,
                              message: 'At most 20 segments',
                            },
                          ]}
                        >
                          <Select
                            mode="multiple"
                            allowClear
                            placeholder="Everyone"
                            options={segmentOptions(cohortSlugs)}
                          />
                        </Form.Item>
                      </Col>
                      <Col span={6}>
                        <Form.Item
                          label="Hide from payers"
                          name={[field.name, 'excludePayers']}
                          valuePropName="checked"
                        >
                          <Switch />
                        </Form.Item>
                      </Col>
                      <Col span={6}>
                        <Form.Item label="Minimum level" name={[field.name, 'minLevel']}>
                          <InputNumber
                            min={1}
                            max={MAX_MIN_LEVEL}
                            precision={0}
                            placeholder="None"
                            style={{ width: '100%' }}
                          />
                        </Form.Item>
                      </Col>
                    </Row>

                    <Row gutter={16}>
                      <Col span={12}>
                        <Form.Item
                          label="Experiment"
                          name={[field.name, 'experiment', 'key']}
                          extra="Makes this slot a test arm. Arms share one Slot Index and one experiment."
                        >
                          <Select
                            allowClear
                            showSearch
                            placeholder="Not a test arm"
                            options={experiments.map((experiment) => ({
                              value: experiment.key,
                              label: `${experiment.key} (${experiment.status})`,
                            }))}
                            onChange={() =>
                              form.setFieldValue(
                                ['mMAd', field.name, 'experiment', 'variant'],
                                undefined
                              )
                            }
                          />
                        </Form.Item>
                      </Col>
                      <Col span={12}>
                        <Form.Item noStyle shouldUpdate>
                          {({ getFieldValue }) => {
                            const key = getFieldValue(['mMAd', field.name, 'experiment', 'key']) as
                              | string
                              | undefined;
                            const variants =
                              experiments.find((experiment) => experiment.key === key)?.variants ??
                              [];
                            return (
                              <Form.Item
                                label="Variant"
                                name={[field.name, 'experiment', 'variant']}
                                dependencies={[['mMAd', field.name, 'experiment', 'key']]}
                                rules={[experimentVariantRule(field.name)]}
                                extra="Players in this variant see this arm; control also shows when the test is off."
                              >
                                <Select
                                  allowClear
                                  disabled={!key}
                                  placeholder={
                                    key ? 'Variant that sees this arm' : 'Pick an experiment first'
                                  }
                                  options={variants.map((variant) => ({
                                    value: variant,
                                    label: variant,
                                  }))}
                                />
                              </Form.Item>
                            );
                          }}
                        </Form.Item>
                      </Col>
                    </Row>

                    <Row gutter={16}>
                      <Col span={12}>
                        <Form.Item
                          label="Only these countries"
                          name={[field.name, 'countries', 'include']}
                          extra="2-letter codes, e.g. IN. Players whose country is unknown do not see it."
                        >
                          <Select
                            mode="tags"
                            allowClear
                            tokenSeparators={[',', ' ']}
                            placeholder="Every country"
                          />
                        </Form.Item>
                      </Col>
                      <Col span={12}>
                        <Form.Item
                          label="Not these countries"
                          name={[field.name, 'countries', 'exclude']}
                          extra="Players whose country is unknown still see it."
                        >
                          <Select
                            mode="tags"
                            allowClear
                            tokenSeparators={[',', ' ']}
                            placeholder="None"
                          />
                        </Form.Item>
                      </Col>
                    </Row>

                    <Text strong>Ads in this slot:</Text>
                    <Form.List name={[field.name, 'data']}>
                      {(adFields, { add: addAd, remove: removeAd }) => (
                        <div style={{ marginTop: 8 }}>
                          {adFields.map((adField) => (
                            <div
                              key={adField.key}
                              style={{
                                position: 'relative',
                                marginBottom: 20,
                                border: '1px dashed #d9d9d9',
                                padding: 8,
                                borderRadius: 4,
                              }}
                            >
                              <div style={{ textAlign: 'right', marginBottom: 4 }}>
                                <MinusCircleOutlined
                                  onClick={() => removeAd(adField.name)}
                                  style={{ color: 'red' }}
                                />
                              </div>
                              <Row gutter={16}>
                                <Col span={8}>
                                  <Form.Item
                                    name={[adField.name, 'adType']}
                                    label="Type"
                                    rules={[{ required: true }]}
                                  >
                                    <InputNumber style={{ width: '100%' }} />
                                  </Form.Item>
                                </Col>
                                <Col span={8}>
                                  <Form.Item
                                    name={[adField.name, 'id']}
                                    label="Prod ID"
                                    rules={[{ required: true }]}
                                  >
                                    <Input />
                                  </Form.Item>
                                </Col>
                                <Col span={8}>
                                  <Form.Item
                                    name={[adField.name, 'ename']}
                                    label="Event"
                                    rules={[{ required: true }]}
                                  >
                                    <Input />
                                  </Form.Item>
                                </Col>
                              </Row>
                              <Form.Item
                                name={[adField.name, 'imgurl']}
                                label="Image URL"
                                rules={[{ required: true }]}
                              >
                                <Input />
                              </Form.Item>
                              <Form.Item
                                name={[adField.name, 'imgurlIos']}
                                label="iOS Image URL (optional)"
                                extra="iPhone players see this image instead; use it when the banner shows a price."
                              >
                                <Input allowClear />
                              </Form.Item>
                              <Row gutter={16}>
                                <Col span={12}>
                                  <Form.Item
                                    name={[adField.name, 'isStatic']}
                                    label="Is Static"
                                    valuePropName="checked"
                                  >
                                    <Switch />
                                  </Form.Item>
                                </Col>
                                <Col span={12}>
                                  <Form.Item
                                    name={[adField.name, 'canHideBuyButton']}
                                    label="Can Hide Buy Button"
                                    valuePropName="checked"
                                  >
                                    <Switch />
                                  </Form.Item>
                                </Col>
                              </Row>
                            </div>
                          ))}
                          <Button
                            type="dashed"
                            onClick={() => addAd()}
                            block
                            icon={<PlusOutlined />}
                          >
                            Add Ad to Slot
                          </Button>
                        </div>
                      )}
                    </Form.List>
                  </Card>
                ))}
                <Button type="dashed" onClick={() => add()} block icon={<PlusOutlined />}>
                  Add Slot
                </Button>
                <Text type="secondary">
                  Targeted slots are filtered per player. A change reaches a device at its next app
                  launch. Country comes from the player's GA4 location (player_locale), not the
                  device header.
                </Text>
                <Text type="secondary">
                  Test arms: slots on one Slot Index tagged with the same experiment. A player sees
                  the first arm for their variant, and the control arm while the test is off.
                  Deleting an arm while its test runs leaves that variant's players with nothing on
                  that index; the save asks first.
                </Text>
              </div>
            )}
          </Form.List>
        </Form>
      </Modal>
    </div>
  );
};

export default CustomAdsPage;
